import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const Footer = () => {
  const currentYear = new Date()?.getFullYear();

  const footerSections = [
    {
      title: 'Reviews & Guides',
      links: [
        { name: 'Latest Reviews', path: '/product-review' },
        { name: 'Buying Guides', path: '/buying-guide' },
        { name: 'Comparison Tool', path: '/comparison-tool' },
        { name: 'Best Vacuum Lists', path: '/product-review' },
        { name: 'Testing Methodology', path: '/buying-guide' }
      ]
    },
    {
      title: 'Vacuum Types',
      links: [
        { name: 'Cordless Vacuums', path: '/product-review' },
        { name: 'Robot Vacuums', path: '/product-review' },
        { name: 'Upright Vacuums', path: '/product-review' },
        { name: 'Canister Vacuums', path: '/product-review' },
        { name: 'Handheld Vacuums', path: '/product-review' }
      ]
    },
    {
      title: 'Special Needs',
      links: [
        { name: 'Pet Hair Removal', path: '/buying-guide' },
        { name: 'Allergy Relief', path: '/buying-guide' },
        { name: 'Hardwood Floors', path: '/buying-guide' },
        { name: 'Deep Carpet', path: '/buying-guide' },
        { name: 'Small Apartments', path: '/buying-guide' }
      ]
    },
    {
      title: 'Deals & Support',
      links: [
        { name: 'Current Deals', path: '/deal-tracker' },
        { name: 'Price Tracker', path: '/deal-tracker' },
        { name: 'Deal Alerts', path: '/deal-tracker' },
        { name: 'Customer Support', path: '/homepage' },
        { name: 'Contact Us', path: '/homepage' }
      ]
    }
  ];

  const socialLinks = [
    { name: 'Facebook', icon: 'Facebook', url: '#' },
    { name: 'Twitter', icon: 'Twitter', url: '#' },
    { name: 'Instagram', icon: 'Instagram', url: '#' },
    { name: 'YouTube', icon: 'Youtube', url: '#' },
    { name: 'Pinterest', icon: 'PinIcon', url: '#' }
  ];

  const trustBadges = [
    {
      icon: 'Shield',
      text: 'SSL Secured'
    },
    {
      icon: 'Award',
      text: 'FTC Compliant'
    },
    {
      icon: 'CheckCircle',
      text: 'Verified Reviews'
    }
  ];

  return (
    <footer className="bg-gray-900 text-white">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <Link to="/homepage" className="flex items-center space-x-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 bg-primary rounded-lg">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="text-white"
                >
                  <path
                    d="M12 2L2 7V17L12 22L22 17V7L12 2Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 22V12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <path
                    d="M2 7L12 12L22 7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle
                    cx="12"
                    cy="9"
                    r="2"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                </svg>
              </div>
              <div>
                <span className="text-xl font-bold">Vacuum Guide</span>
                <div className="text-xs text-gray-400">Expert Reviews</div>
              </div>
            </Link>
            
            <p className="text-gray-300 mb-6 leading-relaxed">
              Your trusted source for honest vacuum reviews, expert comparisons, and smart buying decisions. 
              We test every product thoroughly to help you find the perfect cleaning solution.
            </p>

            {/* Social Links */}
            <div className="flex space-x-4 mb-6">
              {socialLinks?.map((social) => (
                <a
                  key={social?.name}
                  href={social?.url}
                  className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-primary transition-colors duration-200"
                  aria-label={social?.name}
                >
                  <Icon name={social?.icon} size={20} />
                </a>
              ))}
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-4">
              {trustBadges?.map((badge, index) => (
                <div key={index} className="flex items-center space-x-2 text-sm text-gray-400">
                  <Icon name={badge?.icon} size={16} className="text-green-400" />
                  <span>{badge?.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Footer Links */}
          {footerSections?.map((section, index) => (
            <div key={index}>
              <h3 className="text-lg font-semibold mb-4">{section?.title}</h3>
              <ul className="space-y-3">
                {section?.links?.map((link) => (
                  <li key={link?.name}>
                    <Link
                      to={link?.path}
                      className="text-gray-300 hover:text-white transition-colors duration-200 text-sm"
                    >
                      {link?.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
      {/* Newsletter Section */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="mb-4 md:mb-0">
              <h3 className="text-lg font-semibold mb-2">Stay Updated</h3>
              <p className="text-gray-300 text-sm">
                Get the latest reviews and exclusive deals delivered to your inbox.
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2 text-sm text-gray-400">
                <Icon name="Users" size={16} />
                <span>500K+ subscribers</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-gray-400">
                <Icon name="Star" size={16} className="text-yellow-500" />
                <span>4.9/5 rating</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Bottom Footer */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between text-sm text-gray-400">
            <div className="mb-4 md:mb-0">
              <p>© {currentYear} Vacuum Guide. All rights reserved.</p>
            </div>
            
            <div className="flex flex-wrap items-center space-x-6">
              <Link to="/homepage" className="hover:text-white transition-colors duration-200">
                Privacy Policy
              </Link>
              <Link to="/homepage" className="hover:text-white transition-colors duration-200">
                Terms of Service
              </Link>
              <Link to="/homepage" className="hover:text-white transition-colors duration-200">
                Affiliate Disclosure
              </Link>
              <Link to="/homepage" className="hover:text-white transition-colors duration-200">
                Cookie Policy
              </Link>
            </div>
          </div>
          
          <div className="mt-4 pt-4 border-t border-gray-800 text-xs text-gray-500">
            <p>
              Vacuum Guide is a participant in the Amazon Services LLC Associates Program and other affiliate programs.
              We may earn commissions from qualifying purchases made through our links. This helps support our testing and review process at no cost to you.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;